import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, getErrorMessage } from '../utils/api';
import { showToast } from '../utils/toast';
import { browserSubscription, deviceLabel, urlBase64ToUint8Array } from '../utils/pushSupport';

export type PushGroup =
  | 'rework'
  | 'competencies'
  | 'performanceReview'
  | 'essays'
  | 'scoring'
  | 'goals';
export const PUSH_GROUPS: PushGroup[] = [
  'rework',
  'competencies',
  'performanceReview',
  'essays',
  'scoring',
  'goals',
];

export interface PushDevice {
  id: string;
  deviceLabel: string;
  createdAt: string;
  /** Последний раз сервис доставки принял пуш — не «показан на экране». */
  lastSentAt: string | null;
  endpoint: string;
}

export interface PushSettings {
  isConfigured: boolean;
  publicKey: string | null;
  groups: PushGroup[];
  devices: PushDevice[];
}

const SETTINGS_KEY = ['push', 'settings'];

export const usePushSettings = (enabled = true) =>
  useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: (): Promise<PushSettings> => api('/push/settings'),
    enabled,
  });

const sendSubscription = (subscription: PushSubscription) => {
  const json = subscription.toJSON();
  return api('/push/subscriptions', {
    method: 'POST',
    body: { endpoint: json.endpoint, keys: json.keys, deviceLabel: deviceLabel() },
  });
};

/** Подписать это устройство. Разрешение браузер спрашивает только здесь — по клику. */
export async function subscribeThisDevice(publicKey: string) {
  if ((await Notification.requestPermission()) !== 'granted') return;
  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    }));
  await sendSubscription(subscription);
}

/** Кнопка «Выйти»; выход по отвергнутому токену отписывает сам (`userStore.logout`). */
export async function unsubscribeThisDevice() {
  try {
    await (await browserSubscription())?.unsubscribe();
  } catch (e) {
    // Отписка — забота, а не условие выхода: выйти можно в любом случае.
    console.warn('[push] unsubscribe failed', e);
  }
}

/**
 * При загрузке: обновить на сервере подписку этого браузера, только если сервер
 * уже числит её за текущим человеком. Чужую или новую — никогда: разрешение
 * браузер выдаёт сайту, а не человеку, и на общем компьютере следующий
 * пользователь оказался бы подписан без согласия.
 */
export async function refreshOwnSubscription() {
  const subscription = await ownSubscription();
  if (subscription) await sendSubscription(subscription);
}

/** Подписка этого браузера, если сервер числит её за текущим человеком; иначе null. */
export async function ownSubscription() {
  if (!('Notification' in window) || Notification.permission !== 'granted') return null;
  const subscription = await browserSubscription();
  if (!subscription) return null;
  const settings: PushSettings = await api('/push/settings');
  if (!settings.isConfigured) return null;
  return settings.devices.some((d) => d.endpoint === subscription.endpoint) ? subscription : null;
}

/**
 * Смена компании: подписка браузера переезжает к учётке в новой компании, если была у прежней.
 * Уведомления приходят только из выбранной компании — у устройства одна подписка.
 */
export const moveSubscription = sendSubscription;

const usePushMutation = <T>(fn: (arg: T) => Promise<unknown>) => {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: SETTINGS_KEY }),
    onError: (error) =>
      showToast({
        title: t('common.error'),
        message: getErrorMessage(error, t('push.failed')),
        color: 'red',
      }),
  });
};

export const useSubscribeDevice = () =>
  usePushMutation((publicKey: string) => subscribeThisDevice(publicKey));

export const useRemoveDevice = () =>
  usePushMutation(async (device: PushDevice) => {
    // Удаляют это же устройство — отписать и браузер, иначе он продолжит считать себя подписанным.
    if ((await browserSubscription())?.endpoint === device.endpoint) await unsubscribeThisDevice();
    await api(`/push/subscriptions/${device.id}`, { method: 'DELETE' });
  });

export const useSetPushGroups = () =>
  usePushMutation((groups: PushGroup[]) =>
    api('/push/groups', { method: 'PUT', body: { groups } }),
  );

export const useSendTestPush = () =>
  usePushMutation((_: void) => api('/push/test', { method: 'POST' }));
